import { createTheme } from "@mui/material/styles";
import { environment } from "./env";

// EVERY COLOR HERE IS A PILLARS THEME TOKEN FROM THE ANGULAR APP
// (`src/assets/styles/theme/pillars-light|pillars-dark/theme.css`, PLUS THE LAYOUT
// OVERRIDES IN `assets/styles/layout`), SO THE PILOT DRAWS WHAT PRIMENG DRAWS.
// PILLARS BLUE ITSELF STILL COMES FROM THE RUNTIME `primary_color`.
export const PILLARS = {
  light: {
    ground: "#efefef", // --surface-ground (PAGE BACKGROUND)
    card: "#ffffff", // --surface-card / --surface-overlay
    border: "#dee2e6", // --surface-border
    hover: "#e9ecef", // --surface-hover
    text: "#212529", // --text-color
    textSecondary: "#6c757d", // --text-color-secondary
    gray500: "#adb5bd", // --gray-500 (TAB LABELS)
    inputText: "#495057", // .p-inputtext
    inputBorder: "#ced4da",
    tooltip: "#212529",
    tooltipText: "#ffffff",
    primaryHover: "#0069d9", // .p-button:hover
    primaryDark: "#0056b3", // --primary-700
    progressTrack: "#efefef", // PILLARS' `.p-progressbar` OVERRIDE: `--surface-ground`
    success: "#28a745",
    danger: "#dc3545",
    warning: "#ffc107",
    info: "#17a2b8",
    severityText: "#ffffff",
    warningText: "#212529",
  },
  dark: {
    ground: "#20262e",
    card: "#2a323d",
    border: "#3f4b5b",
    hover: "rgba(255, 255, 255, 0.04)",
    text: "rgba(255, 255, 255, 0.87)",
    textSecondary: "rgba(255, 255, 255, 0.6)",
    gray500: "#797d82",
    inputText: "rgba(255, 255, 255, 0.87)",
    inputBorder: "#3f4b5b",
    tooltip: "#3f4b5b",
    tooltipText: "rgba(255, 255, 255, 0.87)",
    primaryHover: "#0069d9",
    primaryDark: "#007bcc",
    progressTrack: "#20262e",
    // THE DARK THEME SWAPS THE SEVERITIES FOR PASTELS WITH NEAR-BLACK TEXT
    success: "#9fdaa8",
    danger: "#f19ea6",
    warning: "#ffe082",
    info: "#7fd8e6",
    severityText: "#151515",
    warningText: "#151515",
  },
} as const;

const scheme = (t: (typeof PILLARS)["light" | "dark"]) => ({
  palette: {
    // BUTTONS STAY WHITE-ON-BLUE IN BOTH THEMES (`.p-button` IN PILLARS-DARK IS `#ffffff` ON `#009aff`)
    primary: { main: environment.primaryColor, dark: t.primaryHover, contrastText: "#ffffff" },
    secondary: { main: "#6c757d", contrastText: "#ffffff" },
    success: { main: t.success, contrastText: t.severityText },
    error: { main: t.danger, contrastText: t.severityText },
    warning: { main: t.warning, contrastText: t.warningText },
    info: { main: t.info, contrastText: t.severityText },
    background: { default: t.ground, paper: t.card },
    text: { primary: t.text, secondary: t.textSecondary },
    divider: t.border,
    action: { hover: t.hover },
  },
});

const L = PILLARS.light;
const D = PILLARS.dark;

export const theme = createTheme({
  // A CLASS ON <html> SELECTS THE SCHEME, SO THE USER'S TOGGLE WINS OVER THE OS SETTING
  cssVariables: { colorSchemeSelector: "class" },
  colorSchemes: { light: scheme(L), dark: scheme(D) },
  // PRIMEFLEX'S BREAKPOINTS, SO `md`/`lg`/`xl` MEAN WHAT `md:`/`lg:`/`xl:` MEAN IN THE ANGULAR TEMPLATES
  breakpoints: { values: { xs: 0, sm: 576, md: 768, lg: 992, xl: 1200 } },
  typography: {
    fontFamily: 'Metropolis, "Helvetica Neue Light", "Helvetica Neue", Helvetica, Arial, "Lucida Grande", sans-serif',
    // THE ANGULAR APP RUNS AT A 14PX ROOT (`$scale`, SET ON <html> BY `_main.scss`). TELLING
    // MUI THE SAME KEEPS ITS REM MATH EXACT, AND BODY TEXT IS 1REM = 14PX WITH THE NORMAL
    // LINE HEIGHT PRIMENG PAGES USE.
    htmlFontSize: 14,
    fontSize: 14,
    body1: { fontSize: "1rem", lineHeight: "normal" },
    body2: { fontSize: "1rem", lineHeight: "normal" },
    // PRIMENG BUTTONS ARE NOT UPPERCASE AND DO NOT BOLD THEIR LABELS
    button: { textTransform: "none", fontWeight: 400, fontSize: "1rem" },
  },
  // PRIMENG CONTROLS (`--border-radius`); LAYOUT CARDS ARE 12PX, SET ON PAPER BELOW
  shape: { borderRadius: 4 },
  components: {
    // ONE PILLARS TOKEN MUI HAS NO PALETTE SLOT FOR: `--gray-500`, USED FOR UNHOSTED
    // PLAYER ICONS. THE DARK VALUE APPLIES UNDER THE `dark` CLASS ON <html>.
    MuiCssBaseline: {
      styleOverrides: {
        // AN ALIAS FOR THE REAL TOKEN IN `styles/pillars-theme-vars.css`
        ":root": { "--pillars-gray-500": "var(--gray-500)" },
      },
    },
    MuiPaper: {
      styleOverrides: {
        // MUI LIGHTENS RAISED SURFACES IN DARK MODE WITH AN OVERLAY GRADIENT; THE
        // PILLARS DARK THEME USES ONE COLOR FOR CARDS AND OVERLAYS
        root: { backgroundImage: "none" },
        // `.card shadow-1`: CARD SURFACE, 1PX `--surface-border`, 12PX RADIUS AND
        // PRIMEFLEX'S `shadow-1` (THE SAME SOFT SHADOW THE TOP BAR USES)
        outlined: {
          borderRadius: 12,
          boxShadow: "0px 3px 5px rgba(0, 0, 0, 0.02), 0px 0px 2px rgba(0, 0, 0, 0.05), 0px 1px 4px rgba(0, 0, 0, 0.08)",
        },
      },
    },
    MuiButton: {
      defaultProps: { disableElevation: true },
    },
    // `.p-inputswitch` AT ANGULAR'S 14PX ROOT: A 42 x 24.5PX PILL WITH THE KNOB INSIDE.
    // OFF: `#ced4da` (DARK `#3f4b5b`) WITH A WHITE KNOB. ON: PILLARS BLUE, AND IN DARK
    // MODE THE KNOB TURNS `#151515`. LIKE `.p-inputswitch` IT IS AN UNMARGINED INLINE-BLOCK
    // WITH NO TEXT, SO IN A BLOCK PARENT IT SITS ON THE BASELINE AND THE LINE KEEPS ITS
    // DESCENDER SPACE BELOW (FLEX PARENTS BLOCKIFY IT, AS THEY DO THE PRIMENG ONE); THE TRACK
    // IS BLOCK SO IT STILL FILLS THE ROOT.
    MuiSwitch: {
      styleOverrides: {
        root: { display: "inline-block", verticalAlign: "baseline", width: 42, height: 24.5, padding: 0, overflow: "visible", margin: 0, "&:has(.Mui-disabled)": { opacity: 0.65 } },
        switchBase: ({ theme }) => ({
          padding: 3.5,
          // THE OFF KNOB IS WHITE IN BOTH THEMES (MUI DEFAULTS IT TO GRAY IN DARK)
          color: "#ffffff",
          "&.Mui-checked": {
            transform: "translateX(17.5px)",
            color: "#ffffff",
            "& + .MuiSwitch-track": { backgroundColor: theme.palette.primary.main, opacity: 1 },
          },
          "&:hover": { backgroundColor: "transparent" },
          // `.p-disabled`: THE SAME COLORS, THE WHOLE SWITCH AT 0.65 (MUI WOULD GRAY THE KNOB
          // AND FADE THE TRACK TO 0.12)
          "&.Mui-disabled": { color: "#ffffff" },
          "&.Mui-disabled + .MuiSwitch-track": { opacity: 1 },
          "&.Mui-checked.Mui-disabled + .MuiSwitch-track": { opacity: 1 },
          ...theme.applyStyles("dark", { "&.Mui-checked": { color: "#151515" }, "&.Mui-disabled.Mui-checked": { color: "#151515" } }),
        }),
        thumb: { width: 17.5, height: 17.5, boxShadow: "none", backgroundColor: "currentColor" },
        track: ({ theme }) => ({
          display: "block",
          borderRadius: 30,
          opacity: 1,
          backgroundColor: "#ced4da",
          ...theme.applyStyles("dark", { backgroundColor: "#3f4b5b" }),
        }),
      },
    },
    MuiTooltip: {
      styleOverrides: {
        // `_overrides.scss`: `--primary-color-tooltip` (#3724fe) WITH WHITE TEXT IN BOTH
        // THEMES; THE THEME'S 0.5REM 0.75REM PADDING AND 4PX RADIUS
        tooltip: {
          backgroundColor: "var(--primary-color-tooltip)",
          color: "#ffffff",
          fontSize: "1rem",
          fontWeight: 400,
          padding: "0.5rem 0.75rem",
          borderRadius: 4,
        },
        arrow: { color: "var(--primary-color-tooltip)" },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: ({ theme }) => ({
          backgroundColor: L.progressTrack,
          ...theme.applyStyles("dark", { backgroundColor: D.progressTrack }),
        }),
      },
    },
    // `.p-inputtext` WITH PILLARS' "FILLED" INPUT STYLE: GROUND-COLORED FIELD, GRAY
    // BORDER, BLUE BORDER ON HOVER AND FOCUS, AND THE THEME'S FOCUS RING
    MuiOutlinedInput: {
      styleOverrides: {
        root: ({ theme }) => ({
          backgroundColor: L.ground,
          color: L.inputText,
          "& .MuiOutlinedInput-notchedOutline": { borderColor: L.inputBorder },
          "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: theme.palette.primary.main },
          "&.Mui-focused": { boxShadow: "0 0 0 0.2rem rgba(38, 143, 255, 0.5)" },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: theme.palette.primary.main, borderWidth: 1 },
          ...theme.applyStyles("dark", {
            backgroundColor: D.ground,
            color: D.inputText,
            "& .MuiOutlinedInput-notchedOutline": { borderColor: D.inputBorder },
            "&.Mui-focused": { boxShadow: "0 0 0 1px #e3f3fe" },
          }),
        }),
      },
    },
    // `.p-datatable`: THEMED BORDERS, HEADER IN TEXT COLOR ON THE CARD SURFACE
    MuiTableCell: {
      styleOverrides: {
        root: ({ theme }) => ({ borderColor: L.border, ...theme.applyStyles("dark", { borderColor: D.border }) }),
        head: { fontWeight: 700 },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: ({ theme }) => ({
          "&.MuiTableRow-hover:hover": { backgroundColor: L.hover },
          ...theme.applyStyles("dark", { "&.MuiTableRow-hover:hover": { backgroundColor: D.hover } }),
        }),
      },
    },
    // PILLARS' TAB OVERRIDE (`_overrides.scss`): UPPERCASE, SMALLER, `--gray-500`;
    // THE ACTIVE TAB IN PILLARS BLUE
    MuiTab: {
      styleOverrides: {
        root: ({ theme }) => ({
          textTransform: "uppercase",
          fontSize: "smaller",
          color: L.gray500,
          "&.Mui-selected": { color: theme.palette.primary.main },
          ...theme.applyStyles("dark", { color: D.gray500 }),
        }),
      },
    },
    // THE SIDE MENU (`_menu.scss`): TEXT-COLORED ITEMS, 12PX RADIUS, HOVER SURFACE;
    // THE CURRENT ROUTE IS BOLD PILLARS BLUE WITH NO FILL
    MuiListItemButton: {
      styleOverrides: {
        root: ({ theme }) => ({
          borderRadius: 12,
          "&:hover": { backgroundColor: L.hover },
          "&.Mui-selected, &.Mui-selected:hover": { backgroundColor: "transparent", color: theme.palette.primary.main, fontWeight: 700 },
          "&.Mui-selected .MuiListItemIcon-root, &.Mui-selected .MuiListItemText-primary": { color: theme.palette.primary.main, fontWeight: 700 },
          "&.Mui-selected:hover": { backgroundColor: L.hover },
          ...theme.applyStyles("dark", { "&:hover, &.Mui-selected:hover": { backgroundColor: D.hover } }),
        }),
      },
    },
  },
});
